import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';

import { useCornerName } from '../CornerNames';

/**
 * Competition Design System — the canonical AKA / SHIRO position badge.
 *
 * The role belongs to the POSITION, never to the athlete: the upper slot of
 * a match is always AKA (red), the lower is always SHIRO (white). AKA/SHIRO
 * are proper karate terms and stay UNTRANSLATED; only the accessible label
 * ("AKA competitor" / "SHIRO competitor") describes them for screen readers.
 *
 * Reused by CompetitorCard, MatchCard and — in time — the schedule, live
 * results, match view, scoreboard and PDF. Never re-implement AKA/SHIRO.
 */
export const AKA = 'AKA';
export const SHIRO = 'SHIRO';

const SIZE = {
    sm: { font: 9, dot: 6, padV: 1, padH: 4 },
    md: { font: 11, dot: 8, padV: 2, padH: 6 },
    lg: { font: 14, dot: 10, padV: 3, padH: 10 },
};

const useStyles = makeStyles(() => ({
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        borderRadius: 3,
        fontWeight: 700,
        letterSpacing: 0.5,
        lineHeight: 1,
        textTransform: 'uppercase',
        userSelect: 'none',
    },
    dot: {
        borderRadius: '50%',
        flexShrink: 0,
    },
    // AKA = red side.
    akaSolid: { backgroundColor: '#c62828', color: '#fff' },
    akaOutline: { border: '1px solid #c62828', color: '#c62828', backgroundColor: 'transparent' },
    akaDot: { backgroundColor: '#c62828' },
    // SHIRO = white side (rendered light with a grey border so it reads on paper too).
    shiroSolid: { backgroundColor: '#eceff1', color: '#37474f', border: '1px solid #b0bec5' },
    shiroOutline: { border: '1px solid #90a4ae', color: '#546e7a', backgroundColor: 'transparent' },
    shiroDot: { backgroundColor: '#eceff1', border: '1px solid #90a4ae' },
}));

const CompetitorPositionBadge = props => {
    const { position, size, variant, showDot, className } = props;
    const classes = useStyles();
    // The WORD is the competition's (CornerNames); the position stays the
    // role the code reasons about.
    const cornerName = useCornerName();
    const isAka = position === AKA;
    const dims = SIZE[size] || SIZE.md;

    const badgeStyle = {
        fontSize: dims.font,
        padding: `${dims.padV}px ${dims.padH}px`,
    };
    const dotStyle = { width: dims.dot, height: dims.dot };

    const colorClass = isAka
        ? (variant === 'outline' ? classes.akaOutline : classes.akaSolid)
        : (variant === 'outline' ? classes.shiroOutline : classes.shiroSolid);
    const dotClass = isAka ? classes.akaDot : classes.shiroDot;

    return (
        <span
            className={[classes.badge, colorClass, className].filter(Boolean).join(' ')}
            style={badgeStyle}
            data-testid='competitor-position-badge'
            data-position={position}
            role='img'
            aria-label={`${position} competitor`}
        >
            {showDot && <span className={[classes.dot, dotClass].join(' ')} style={dotStyle} aria-hidden='true' />}
            {cornerName(position)}
        </span>
    );
};

CompetitorPositionBadge.propTypes = {
    position: PropTypes.oneOf([AKA, SHIRO]).isRequired,
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
    variant: PropTypes.oneOf(['solid', 'outline']),
    showDot: PropTypes.bool,
    className: PropTypes.string,
};

CompetitorPositionBadge.defaultProps = {
    size: 'md',
    variant: 'solid',
    showDot: true,
    className: null,
};

export default CompetitorPositionBadge;
